<template>
  <div class="configuration-tab">
    <app-loading :loading="isLoading" is-background />

    <ValidationObserver v-if="!isLoading" v-slot="v">
      <b-form id="edit-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
        <div class="form-container">
          <!-- Basic Details -->
          <basic-details-section
            :name="awardForm.name"
            :selected-division-ids="selectedDivisionIds"
            :division-options="divisionOptions"
            @update:name="awardForm.name = $event"
            @update:selectedDivisionIds="onDivisionsChange"
          >
            <template #after-divisions>
              <hr />

              <div class="company-values-container">
                <div class="label-row">
                  <div class="label">
                    Add your company values (optional)
                    <small>
                      Tag relevant values to help employees understand what behaviours this award recognises. You can
                      use our suggestions or customise them to match your organisation's values.
                    </small>
                  </div>
                  <b-form-checkbox v-model="companyValuesEnabled" switch />
                </div>
                <div v-if="companyValuesEnabled">
                  <create-company-values-modal v-model="companyValues" />
                </div>
              </div>

              <hr />

              <div class="short-description-container">
                <div class="label">
                  Short description (optional)
                  <small>
                    A short description with extra detail about this award — for example its location or budget source.
                  </small>
                </div>
                <app-input
                  v-model="shortDescription"
                  type="text"
                  maxlength="500"
                  placeholder="Add a short description (optional)"
                />
              </div>
            </template>
          </basic-details-section>

          <!-- Award Details -->
          <div>
            <h4>Award details</h4>
            <sme-card large>
              <monetary-reward-section
                :enabled="monetaryRewardEnabled"
                :currency-symbol="currencySymbol"
                :amounts="awardValueMinor"
                :default-amount-minor="defaultAwardValueMinor"
                :amount-error="awardValueError"
                :redemption-options="selectedRedemptionOptions"
                :redemption-error="redemptionSelectionError"
                :expiration-months="expirationMonths"
                @update:enabled="monetaryRewardEnabled = $event"
                @update:amounts="awardValueMinor = $event"
                @update:defaultAmountMinor="onDefaultAwardValueMinorChange"
                @update:redemptionOptions="selectedRedemptionOptions = $event"
                @update:expirationMonths="expirationMonths = $event"
              >
                <template #reward-amounts>
                  <div class="amount-mode-container">
                    <b-form-radio v-model="amountMode" name="amount-mode" value="fixed">
                      Set fixed reward amount(s)
                    </b-form-radio>
                    <b-form-radio v-model="amountMode" name="amount-mode" value="free">
                      Set adjustable reward amount
                    </b-form-radio>
                  </div>

                  <div v-if="amountMode === 'fixed'" class="reward-amount-container">
                    <div class="label">
                      Reward amount options
                      <small>
                        Choose one or more values that can be awarded with this prize. If you select multiple, the
                        person suggesting a reward can choose the amount.
                      </small>
                    </div>
                    <create-reward-amounts-modal
                      :value="awardValueMinor"
                      :default-minor="defaultAwardValueMinor"
                      :currency-symbol="currencySymbol"
                      @input="awardValueMinor = $event"
                      @update:defaultMinor="onDefaultAwardValueMinorChange"
                    />
                    <small v-if="awardValueError" class="text-danger">{{ awardValueError }}</small>
                  </div>

                  <div v-else class="reward-amount-container">
                    <app-input
                      :value="maxAwardValue"
                      type="text"
                      inputmode="numeric"
                      :prepend="currencySymbol"
                      placeholder="e.g. 50"
                      @input="onMaxAwardValueInput"
                    >
                      <template #label>
                        <div class="label">
                          Maximum amount
                          <small>The most a nominator can award.</small>
                        </div>
                      </template>
                    </app-input>
                    <small v-if="maxAwardValueError" class="text-danger">{{ maxAwardValueError }}</small>
                  </div>
                </template>

                <template #after-amounts>
                  <budget-settings-section
                    v-if="monetaryRewardEnabled"
                    :currency-symbol="currencySymbol"
                    :amount="rewardBudgetAmount"
                    :cadence="rewardBudgetCadence"
                    :year-start-month="budgetYearStartMonth"
                    :year-start-day="budgetYearStartDay"
                    :year-start-day-options="budgetYearStartDayOptions"
                    :year-start-disabled="budgetYearStartDisabled"
                    :year-start-month-rules="budgetYearStartMonthRules"
                    :year-start-day-rules="budgetYearStartDayRules"
                    @update:amount="rewardBudgetAmount = $event"
                    @update:cadence="rewardBudgetCadence = $event"
                    @update:yearStartMonth="budgetYearStartMonth = $event"
                    @update:yearStartDay="budgetYearStartDay = $event"
                  />

                  <div v-if="monetaryRewardEnabled" class="categories-container">
                    <div class="label-row">
                      <div class="label">
                        Add spend categories (optional)
                        <small>Let people tag what a reward is for, so you can track spending by category.</small>
                      </div>
                      <b-form-checkbox v-model="categoriesEnabled" switch />
                    </div>
                    <div v-if="categoriesEnabled">
                      <create-categories-modal v-model="categories" />
                      <b-form-checkbox v-model="categoryRequired" class="mt-2" :disabled="!categories.length">
                        Make selecting a category mandatory
                      </b-form-checkbox>
                      <b-form-checkbox v-model="showCategoryEnabled" class="mt-2" :disabled="!categories.length">
                        Show the category to employees in their feed
                      </b-form-checkbox>
                    </div>
                  </div>
                </template>
              </monetary-reward-section>
            </sme-card>
          </div>

          <!-- Nominations & Approvals -->
          <div v-if="monetaryRewardEnabled">
            <h4>Nominations & Approvals</h4>
            <sme-card large>
              <div class="label-permissions-and-approvals">
                Permissions and approvals
                <small>
                  This award is <span class="highlighted-text">open</span>: anyone can send recognition. Recognitions
                  with monetary rewards will require approval from a reviewer.
                </small>
              </div>

              <div class="approver-container">
                <multi-select
                  id="approvers-select"
                  :value="selectedApproverIds"
                  :options="allEmployeeOptions"
                  placeholder="Select who can approve this award"
                  :searchable="true"
                  search-placeholder="Search by name or email"
                  :disabled="employeesLoading"
                  :select-all="false"
                  select-all-text="No approvers available"
                  class="mb-0"
                  @input="onApproversChange"
                >
                  <template #label>
                    <div class="label">Who can approve?</div>
                  </template>
                </multi-select>
                <small v-if="approverSelectionError" class="text-danger">{{ approverSelectionError }}</small>
              </div>
            </sme-card>
          </div>

          <div>
            <h4>Award status</h4>
            <sme-card large>
              <div class="label-row">
                <div class="label">
                  Award active
                  <small
                    >When disabled, this award will not be visible to employees and no new nominations can be
                    made.</small
                  >
                </div>
                <b-form-checkbox v-model="isActive" switch />
              </div>
            </sme-card>
          </div>
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="resetForm"> Reset </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || !hasChanges">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Saving...' : 'Save changes' }}
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import { RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import { useDivisionEmployeeFiltering } from '@/pages/recognition/composables/useDivisionEmployeeFiltering';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import CreateCategoriesModal from '@/pages/recognition/components/CreateCategoriesModal.vue';
import CreateRewardAmountsModal from '@/pages/recognition/components/CreateRewardAmountsModal.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';
import BudgetSettingsSection from '@/pages/recognition/components/BudgetSettingsSection.vue';

const props = defineProps({
  program: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

// Use shared composable
const {
  currencySymbol,
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  monetaryRewardEnabled,
  amountMode,
  awardValueMinor,
  awardValueError,
  maxAwardValue,
  maxAwardValueError,
  defaultAwardValueMinor,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  rewardBudgetAmount,
  rewardBudgetCadence,
  budgetYearStartMonth,
  budgetYearStartDay,
  budgetYearStartDayOptions,
  budgetYearStartDisabled,
  budgetYearStartMonthRules,
  budgetYearStartDayRules,
  categoriesEnabled,
  categories,
  categoryRequired,
  categoriesInitiallyEnabled,
  showCategoryEnabled,
  shortDescription,
  saving,
  errorMessage,
  onMaxAwardValueInput,
  onDefaultAwardValueMinorChange,
  loadDivisions,
  initBudgetYearStart,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  buildBaseProperties,
  getBudgetMinor,
  getGrantExpiryInterval,
  persistBudgetYearStart,
  validateRedemptionOptions,
  validateAwardValues,
  validateBudgetCadence,
} = useAwardForm();

// Division-based employee filtering
const { employeesLoading, allEmployeeOptions, loadEmployeesAndDivisionPairs } =
  useDivisionEmployeeFiltering(selectedDivisionIds);

// Open-specific state
const companyValuesEnabled = ref(false);
const companyValues = ref([]);
const selectedApproverIds = ref([]);
const approverSelectionError = ref('');
const isActive = ref(true);
const isLoading = ref(true);

// Track original values for change detection
const originalValues = ref({});

const { doToast, TOAST_VARIANT } = useToast();

// Detect if form has changes
const hasChanges = computed(() => {
  const current = getCurrentFormState();
  return JSON.stringify(current) !== JSON.stringify(originalValues.value);
});

/**
 * Parse ISO 8601 duration to months.
 * Handles both month format ("P12M" -> 12) and day format ("P360D" -> 12).
 */
const parseGrantExpiryInterval = interval => {
  if (!interval) {
    return '';
  }
  const str = String(interval);
  // Try month format first (P12M)
  const monthMatch = str.match(/^P(\d+)M$/);
  if (monthMatch) {
    return monthMatch[1];
  }
  // Try day format (P360D) - convert days to months (30 days per month)
  const dayMatch = str.match(/^P(\d+)D$/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    const months = Math.round(days / 30);
    return months > 0 ? String(months) : '';
  }
  return '';
};

/**
 * Get current form state for comparison
 */
const getCurrentFormState = () => ({
  name: awardForm.value.name,
  selectedDivisionIds: [...(selectedDivisionIds.value || [])].sort(),
  companyValuesEnabled: companyValuesEnabled.value,
  companyValues: [...(companyValues.value || [])].sort(),
  categoriesEnabled: categoriesEnabled.value,
  categories: [...(categories.value || [])].sort(),
  categoryRequired: categoryRequired.value,
  showCategoryEnabled: showCategoryEnabled.value,
  shortDescription: shortDescription.value,
  monetaryRewardEnabled: monetaryRewardEnabled.value,
  amountMode: amountMode.value,
  awardValueMinor: awardValueMinor.value,
  maxAwardValue: maxAwardValue.value,
  defaultAwardValueMinor: defaultAwardValueMinor.value,
  selectedRedemptionOptions: [...(selectedRedemptionOptions.value || [])].sort(),
  expirationMonths: expirationMonths.value,
  rewardBudgetAmount: rewardBudgetAmount.value,
  rewardBudgetCadence: rewardBudgetCadence.value,
  budgetYearStartMonth: budgetYearStartMonth.value,
  budgetYearStartDay: budgetYearStartDay.value,
  selectedApproverIds: [...(selectedApproverIds.value || [])].sort(),
  isActive: isActive.value,
});

/**
 * Populate form from program data
 */
const populateFromProgram = program => {
  if (!program) {
    return;
  }

  // Basic details
  awardForm.value.name = program.program_name || '';
  isActive.value = program.is_active !== false;

  // Divisions - API returns array of UUIDs or empty array for "all"
  const divisions = program.enabled_divisions || [];
  selectedDivisionIds.value = divisions.length > 0 ? divisions : [];

  // Company values
  const programCompanyValues = program.properties?.company_values || [];
  if (programCompanyValues.length > 0) {
    companyValuesEnabled.value = true;
    companyValues.value = programCompanyValues.map(v => v.name || v);
  } else {
    companyValuesEnabled.value = false;
    companyValues.value = [];
  }

  // Categories (HERO-2978)
  const programCategories = program.properties?.categories || [];
  if (programCategories.length > 0) {
    categoriesEnabled.value = true;
    categories.value = [...programCategories];
    categoryRequired.value = Boolean(program.properties?.category_required);
    // Opt-in (HERO-3008): only shown to recipients when explicitly enabled.
    showCategoryEnabled.value = program.properties?.show_category === true;
    categoriesInitiallyEnabled.value = true;
  } else {
    categoriesEnabled.value = false;
    categories.value = [];
    categoryRequired.value = false;
    showCategoryEnabled.value = false;
    categoriesInitiallyEnabled.value = false;
  }

  // Short description (HERO-2972)
  shortDescription.value = program.properties?.short_description || '';

  // Monetary reward. Fixed mode stores a non-null award_value_minor; free mode stores
  // award_value_minor = null plus a mandatory max_award_value_minor cap. A program with
  // either signal (or an expiry interval) has monetary rewards enabled.
  const fixedAmount = program.properties?.award_value_minor != null;
  const freeCap = program.properties?.max_award_value_minor != null;
  const hasMonetaryReward = fixedAmount || freeCap || Boolean(program.grant_expiry_interval);
  monetaryRewardEnabled.value = hasMonetaryReward;

  if (hasMonetaryReward) {
    if (fixedAmount) {
      amountMode.value = 'fixed';
      awardValueMinor.value = program.properties.award_value_minor;
      defaultAwardValueMinor.value = program.properties.default_award_value_minor;
      maxAwardValue.value = '';
    } else {
      amountMode.value = 'free';
      awardValueMinor.value = undefined;
      defaultAwardValueMinor.value = undefined;
      maxAwardValue.value = freeCap ? String(Math.round(program.properties.max_award_value_minor / 100)) : '';
    }

    // Redemption options
    const validGrantTypes = program.properties?.valid_grant_types || ['VOUCHER'];
    selectedRedemptionOptions.value = validGrantTypes;

    // Expiration
    expirationMonths.value = parseGrantExpiryInterval(program.grant_expiry_interval);

    // Budget
    if (program.budget_minor) {
      rewardBudgetAmount.value = (program.budget_minor / 100).toFixed(2);
    } else {
      rewardBudgetAmount.value = '';
    }
    if (program.properties?.budget_period) {
      rewardBudgetCadence.value = program.properties.budget_period;
    } else {
      rewardBudgetCadence.value = undefined;
    }

    // Approvers
    const approverGroups = program.properties?.approvers || [];
    if (approverGroups.length > 0) {
      const allApproverIds = approverGroups.flatMap(g => g.approvers || []);
      selectedApproverIds.value = allApproverIds;
    } else {
      selectedApproverIds.value = [];
    }
  } else {
    // Clear monetary reward fields when not enabled
    amountMode.value = 'fixed';
    awardValueMinor.value = undefined;
    defaultAwardValueMinor.value = undefined;
    maxAwardValue.value = '';
    selectedRedemptionOptions.value = [];
    expirationMonths.value = '';
    rewardBudgetAmount.value = '';
    rewardBudgetCadence.value = undefined;
    selectedApproverIds.value = [];
  }

  // Store original values for change detection
  originalValues.value = getCurrentFormState();
};

/**
 * Reset form to original program values
 */
const resetForm = () => {
  populateFromProgram(props.program);
  errorMessage.value = '';
  approverSelectionError.value = '';
  awardValueError.value = '';
  redemptionSelectionError.value = '';
};

const buildProperties = (approverIds, redemptionMethods) => {
  const props = buildBaseProperties(redemptionMethods);

  // Set approvers to null when empty to explicitly clear them
  props.approvers = (approverIds || []).length
    ? [
        {
          name: 'Default',
          approvers: (approverIds || []).map(String),
        },
      ]
    : null;

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      props.company_values = values.map(name => ({ name }));
    }
  }

  return props;
};

const buildPayload = (properties, approvers) => ({
  program_name: awardForm.value.name,
  approval_type: (approvers || []).length > 0 ? RECOGNITION_APPROVAL_TYPES.AMOUNT : RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: false,
  enabled_divisions: enabledDivisions.value,
  budget_minor: getBudgetMinor(),
  grant_expiry_interval: getGrantExpiryInterval(),
  is_active: isActive.value,
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const approvers = monetaryRewardEnabled.value ? selectedApproverIds.value || [] : [];
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];

  try {
    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require at least one approver when monetary rewards are enabled
    if (monetaryRewardEnabled.value && (!approvers || approvers.length === 0)) {
      approverSelectionError.value = 'Please select at least one approver.';
      saving.value = false;
      return;
    }
    approverSelectionError.value = '';

    if (!validateAwardValues()) {
      saving.value = false;
      return;
    }

    if (!validateBudgetCadence()) {
      saving.value = false;
      return;
    }

    const approverIds = (approvers || []).map(String);
    const properties = buildProperties(approverIds, redemptionMethods);
    const payload = buildPayload(properties, approvers);

    const budgetResult = await persistBudgetYearStart();
    if (!budgetResult.success) {
      errorMessage.value = budgetResult.error;
      saving.value = false;
      return;
    }

    await ApiClient.updateRecognitionProgram(props.program.recognition_award_program_id, payload);

    doToast('Success', 'Award updated successfully', TOAST_VARIANT.SUCCESS);

    // Update original values to reflect saved state
    originalValues.value = getCurrentFormState();

    emit('updated');
  } catch (e) {
    errorMessage.value = e?.variables?.message || e?.message || 'Failed to update award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = ids => {
  // Approvers can be any company employee (HERO-2974) and open awards have no
  // division-scoped nominator picker, so nothing is pruned on division change.
  selectedDivisionIds.value = ids;
};

const onApproversChange = ids => {
  selectedApproverIds.value = ids;
};

watch(selectedApproverIds, newVal => {
  if (approverSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    approverSelectionError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
    selectedApproverIds.value = [];
    approverSelectionError.value = '';
  }
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([loadDivisions(), ...loadEmployeesAndDivisionPairs()]);
    initBudgetYearStart();
    setupCommonWatchers();
    populateFromProgram(props.program);
  } finally {
    isLoading.value = false;
  }
});

// Watch for program prop changes (e.g., when refreshed from parent)
watch(
  () => props.program,
  newProgram => {
    if (newProgram) {
      populateFromProgram(newProgram);
    }
  },
);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.configuration-tab {
  position: relative;
  min-height: 200px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}
</style>
